import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Table, Tag } from 'antd';
import { toast, Bounce } from 'react-toastify';
import {
  getIncompleteSizeProfilesReportApi, exportIncompleteSizeProfilesReportApi, uniformErrorMessage,
} from 'api/services/uniformManagement';
import { itemRender, handlePageChange, defaultPagination, pageSizeOptions } from 'utils/table';
import { getGenderLabel } from '../../constants';
import ExcelExportButton from 'components/ExcelExportButton';
import ReportFilterBar, { cleanParams } from '../ReportFilterBar';
import useStickyIdentity from '../../components/useStickyIdentity';
import { truncatedText } from '../../components/columns';

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

/** Cost centre, position and uniform take lists; gender is a closed
 *  vocabulary and stays single. No date range: a missing size is a fact about
 *  now, not about a period. */
const FIELDS = ['groupBy', 'casas', 'positions', 'gender', 'items', 'search'];
const PRIMARY_FIELDS = ['groupBy', 'casas', 'positions', 'gender'];

/** The report is asked for "by cost center", so that is the grain it opens on
 *  and the one Clear restores. Employee is the grain a store manager works
 *  from — one line per person with what they still owe. Detail is the flat
 *  list, one row per (employee, uniform), for the export that goes to HR. */
const GROUP_BY_OPTIONS = [
  { label: 'Cost center', value: 'cost_center' },
  { label: 'Employee', value: 'employee' },
  { label: 'Detail (one row per missing size)', value: '' },
];
const groupFor = (value) => (value ? GROUP_BY_OPTIONS.find((o) => o.value === value) || null : null);
const DEFAULT_FILTERS = { group_by: 'cost_center' };

const sumOf = (rows, pick) => rows.reduce((acc, r) => acc + (pick(r) || 0), 0);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const employeeLink = (record) => (
  <Link to={`/employees/${record.company_user_id}`} className="fw-medium">
    {record.employee_name}
  </Link>
);

/** Employees whose size profile is incomplete (A-15 of the 26 Aug minutes):
 *  every (employee, uniform) pair where the employee's position wears the
 *  uniform, the uniform comes in more than one size, and no size is on file.
 *  One-size and sizeless uniforms are never counted — the same rule the size
 *  confirmation prompt applies, so this list and that prompt agree. */
const IncompleteSizeProfilesReport = ({ itemOptions, itemOptionsLoading }) => {
  // Rows and the grain they came back in are one piece of state, as on the
  // other tabs: the columns must describe the rows under them, including
  // while a request for another grain is in flight.
  const [result, setResult] = useState({ rows: [], group: groupFor(DEFAULT_FILTERS.group_by), count: 0 });
  const [loading, setLoading] = useState(false);
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);

  const fetchData = useCallback(() => {
    let ignore = false;
    setLoading(true);
    const params = cleanParams(applied);
    const askedGroup = groupFor(params.group_by);
    // Grouped grains are unpaginated server-side; only the detail pages.
    if (!askedGroup) {
      params.page = pageSettings.page;
      params.page_size = pageSettings.pageSize;
    }
    getIncompleteSizeProfilesReportApi(params).then((response) => {
      if (ignore) return;
      const body = response.data || {};
      setResult({
        rows: (askedGroup ? body.data : body.results) || [],
        group: askedGroup,
        count: body.count || 0,
      });
      if (!askedGroup) setPageSettings((prev) => ({ ...prev, total: body.count || 0 }));
    }).catch((e) => {
      if (ignore) return;
      toast.error(uniformErrorMessage(e, 'Failed to load the incomplete size profiles report'), toastOpts);
      setResult({ rows: [], group: askedGroup, count: 0 });
    }).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [applied, pageSettings.page, pageSettings.pageSize]);

  useEffect(() => fetchData(), [fetchData]);

  const handleChange = (pagination, filters, sorter) => {
    setPageSettings(handlePageChange(pagination, filters, sorter, pageSettings));
  };
  const handleApply = () => {
    setApplied(draft);
    setPageSettings((prev) => ({ ...prev, page: 1 }));
  };
  const handleClear = () => {
    setDraft(DEFAULT_FILTERS);
    setApplied(DEFAULT_FILTERS);
    setPageSettings((prev) => ({ ...prev, page: 1 }));
  };

  /** A cost-centre row becomes the employee grain filtered to that store.
   *  Written to both draft and applied so the bar shows what it applied.
   *  "Unassigned" has no id to filter by, so it does not drill. */
  const drillToEmployees = (record) => {
    if (record.cost_center_id === null || record.cost_center_id === undefined) return;
    const filters = { ...applied, group_by: 'employee', cost_center: String(record.cost_center_id) };
    setDraft(filters);
    setApplied(filters);
    setPageSettings((prev) => ({ ...prev, page: 1 }));
  };

  const pinLeft = useStickyIdentity();
  const grain = result.group?.value || '';

  const columns = useMemo(() => {
    if (grain === 'cost_center') {
      return [
        {
          title: 'Cost Center', dataIndex: 'cost_center_name', key: 'cost_center', ...pinLeft(220),
          render: (name, r) => (
            <span>
              {name}
              {r.cost_center_code && <small className="text-muted ms-2">{r.cost_center_code}</small>}
            </span>
          ),
        },
        {
          title: 'Employees missing a size', dataIndex: 'employees_missing', key: 'employees', align: 'right', width: 220,
          render: (n, r) => (
            r.cost_center_id === null || r.cost_center_id === undefined
              ? <span>{n}</span>
              : (
                <button type="button" className="btn btn-link p-0" onClick={() => drillToEmployees(r)}
                  aria-label={`View the ${n} employees at ${r.cost_center_name} missing a size`}>
                  {n}
                </button>
              )
          ),
        },
        { title: 'Sizes missing', dataIndex: 'uniforms_missing', key: 'gaps', align: 'right', width: 160 },
      ];
    }
    if (grain === 'employee') {
      return [
        {
          title: 'Employee', dataIndex: 'employee_name', key: 'employee', ...pinLeft(240),
          render: (_, r) => (
            <span>
              {employeeLink(r)}
              {r.emp_no && <small className="text-muted ms-2">{r.emp_no}</small>}
            </span>
          ),
        },
        { title: 'Cost Center', dataIndex: 'cost_center_name', key: 'cost_center', ...truncatedText(180) },
        { title: 'Position', dataIndex: 'position_name', key: 'position', ...truncatedText(160) },
        { title: 'Gender', dataIndex: 'gender', key: 'gender', width: 100, render: (g) => getGenderLabel(g) },
        {
          title: 'Missing a size for', dataIndex: 'missing_items', key: 'items',
          render: (items) => (items || []).map((i) => <Tag key={i.id}>{i.name}</Tag>),
        },
        { title: 'Count', dataIndex: 'missing_count', key: 'count', align: 'right', width: 90 },
      ];
    }
    return [
      { title: 'Cost Center', dataIndex: 'cost_center_name', key: 'cost_center', ...pinLeft(200) },
      { title: 'Employee', dataIndex: 'employee_name', key: 'employee', width: 220, render: (_, r) => employeeLink(r) },
      { title: 'Emp No', dataIndex: 'emp_no', key: 'emp_no', width: 110 },
      { title: 'Position', dataIndex: 'position_name', key: 'position', ...truncatedText(160) },
      { title: 'Gender', dataIndex: 'gender', key: 'gender', width: 100, render: (g) => getGenderLabel(g) },
      { title: 'Uniform', dataIndex: 'uniform_item_name', key: 'uniform', ...truncatedText(220) },
    ];
  // drillToEmployees closes over `applied`; recomputed with it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grain, pinLeft, applied]);

  const rowKey = (r) => (
    grain === 'cost_center' ? `cc-${r.cost_center_id ?? 'none'}`
      : grain === 'employee' ? `emp-${r.company_user_id}`
        : `gap-${r.company_user_id}-${r.uniform_item_id}`
  );

  const summary = useMemo(() => {
    const { rows, count } = result;
    if (!rows.length) return null;
    if (grain === 'cost_center') {
      // Nouns only — no verb to agree with "1 employee".
      return `${plural(sumOf(rows, (r) => r.employees_missing), 'employee', 'employees')} across `
        + `${plural(rows.length, 'cost center', 'cost centers')} · `
        + `${plural(sumOf(rows, (r) => r.uniforms_missing), 'size', 'sizes')} missing`;
    }
    if (grain === 'employee') {
      return `${plural(rows.length, 'employee', 'employees')} · `
        + `${plural(sumOf(rows, (r) => r.missing_count), 'size', 'sizes')} missing`;
    }
    return `${plural(count, 'missing size', 'missing sizes')}`;
  }, [result, grain]);

  const filtered = Object.keys(cleanParams(applied)).some((k) => k !== 'group_by');
  const emptyText = filtered
    ? 'No missing sizes match these filters.'
    : 'Every employee has a size on file for every uniform their position wears.';

  const exportParams = cleanParams(applied);
  const exportName = `Incomplete Size Profiles${grain ? `_By${grain === 'cost_center' ? 'CostCenter' : 'Employee'}` : ''}.xlsx`;

  return (
    <div>
      <ReportFilterBar
        fields={FIELDS} primaryFields={PRIMARY_FIELDS}
        value={draft} appliedValue={applied} onChange={setDraft}
        onApply={handleApply} onClear={handleClear}
        groupByOptions={GROUP_BY_OPTIONS} groupByPlaceholder="Cost center"
        itemOptions={itemOptions} itemOptionsLoading={itemOptionsLoading}
        searchPlaceholder="Employee name or number"
      />
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <div>
          {summary && <div className="fw-medium">{summary}</div>}
          <small className="text-muted">
            Counts only uniforms that come in more than one size — a one-size uniform needs no choice.
          </small>
        </div>
        <ExcelExportButton framed apiFn={exportIncompleteSizeProfilesReportApi} params={exportParams}
          filename={exportName} disabled={loading || !result.rows.length} />
      </div>
      <Table
        columns={columns} dataSource={result.rows} rowKey={rowKey} loading={loading}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: loading ? ' ' : emptyText }}
        onChange={grain ? undefined : handleChange}
        pagination={grain
          // Grouped grains arrive whole; page them here so a 600-person
          // employee grain does not become one endless table.
          ? { pageSize: 50, showSizeChanger: false, itemRender, hideOnSinglePage: true }
          : {
            current: pageSettings.page, pageSize: pageSettings.pageSize, total: pageSettings.total,
            showSizeChanger: true, pageSizeOptions, itemRender,
            showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
          }}
      />
    </div>
  );
};

export default IncompleteSizeProfilesReport;
